import { withDefaults } from '../../../lib/teamsDefaults';
import {
  vehicleClingDefaults,
  vehicleDefaults,
  vehicleFuelDefaults,
  vehicleNoiseDefaults,
  vehicleRamDefaults,
} from '../../../lib/s3/defaults';
import {
  SEAT_ROLES,
  type VehicleDef,
  type VehicleMountDef,
  type VehicleSeatDef,
  type WeaponDef,
} from '../../../lib/s3/types';
import {
  CheckboxField,
  Fieldset,
  IssueList,
  ListEditor,
  NumberField,
  NumberInput,
  SelectInput,
  TextField,
  smallBtn,
} from '../fields';
import { useItemOptions } from '../refs';
import type { FormProps } from '../types';
import { KeyValueEditor } from '../s2/kv';

const roleOptions = SEAT_ROLES.map((r) => ({ value: r, label: r }));

/** The .50 cal's page numbers, for "Add a mount". */
const newMount = (): VehicleMountDef => ({
  id: 'hmg_50',
  baseItemId: 'lmg',
  noiseM: 90,
  seat: 'mount',
  roundsPerRun: 300,
  weapon: {
    damage: 55,
    pierce: 2,
    rangeM: 28,
    fireIntervalS: 0.15,
    magazineSize: 100,
    reloadS: 4,
    bloomPerShotDeg: 0.6,
    maxBloomDeg: 6,
  },
});

/** One lent vehicle (lurerun.html "The vehicle ladder"); absent fields show the C# defaults. */
export function VehicleForm({ value, onChange, errors }: FormProps<VehicleDef>) {
  const v = withDefaults(vehicleDefaults(), value) as Required<VehicleDef>;
  const noise = withDefaults(vehicleNoiseDefaults(), v.noise ?? {}) as Required<
    NonNullable<VehicleDef['noise']>
  >;
  const fuel = withDefaults(vehicleFuelDefaults(), v.fuel ?? {}) as Required<
    NonNullable<VehicleDef['fuel']>
  >;
  const ram = withDefaults(vehicleRamDefaults(), v.ram ?? {}) as Required<
    NonNullable<VehicleDef['ram']>
  >;
  const cling = withDefaults(vehicleClingDefaults(), v.cling ?? {}) as Required<
    NonNullable<VehicleDef['cling']>
  >;
  const weapons = useItemOptions((c) => c === 'weapon');
  const set = <K extends keyof VehicleDef>(k: K, x: VehicleDef[K]) => onChange({ ...v, [k]: x });
  const num = (
    label: string,
    path: string,
    val: number | undefined,
    put: (n: number) => void,
    o: { integer?: boolean; hint?: string; min?: number } = {},
  ) => (
    <NumberField
      key={path}
      label={label}
      hint={o.hint}
      integer={o.integer}
      min={o.min ?? 0}
      value={val}
      issues={errors[path]}
      onChange={(n) => put(n ?? 0)}
    />
  );
  const mount = v.mount;
  const weapon: WeaponDef = mount?.weapon ?? {};
  const setMount = (m: Partial<VehicleMountDef>) => set('mount', { ...mount!, ...m });
  const setWeapon = (k: keyof WeaponDef, n: number) => setMount({ weapon: { ...weapon, [k]: n } });

  return (
    <>
      <Fieldset legend="Basics">
        <TextField label="Id" mono value={v.id} issues={errors.id} onChange={(x) => set('id', x)} />
        <TextField
          label="Name"
          value={v.name}
          issues={errors.name}
          onChange={(x) => set('name', x)}
        />
        <TextField
          label="Nickname"
          hint='Shown under the name ("the Hulk"); generic names only (M1).'
          value={v.nickname}
          issues={errors.nickname}
          onChange={(x) => set('nickname', x)}
        />
        <TextField
          label="Model"
          hint="ArenaArt model: Ebike, Motorcycle, Suv, Pickup, Technical."
          mono
          value={v.model}
          issues={errors.model}
          onChange={(x) => set('model', x)}
        />
        {num(
          'Unlock after lure runs',
          'unlockAfterLureRuns',
          v.unlockAfterLureRuns,
          (n) => set('unlockAfterLureRuns', n),
          {
            integer: true,
            hint: 'Extracted lure runs in this run (M12); 0 = from the start.',
          },
        )}
      </Fieldset>
      <Fieldset legend="The drive (a team move)">
        {num('Road speed km/h', 'roadKmh', v.roadKmh, (n) => set('roadKmh', n))}
        {num(
          'Trip energy per km',
          'tripEnergyPerKm',
          v.tripEnergyPerKm,
          (n) => set('tripEnergyPerKm', n),
          {
            hint: 'Walking is 20.',
          },
        )}
      </Fieldset>
      <Fieldset legend="Arena handling">
        {num('Top speed m/s', 'topSpeedMps', v.topSpeedMps, (n) => set('topSpeedMps', n), {
          hint: 'Every rung should outrun a runner (4.3 m/s).',
        })}
        {num('Acceleration m/s²', 'accelMps2', v.accelMps2, (n) => set('accelMps2', n))}
        {num('Turn °/s', 'turnDegPerS', v.turnDegPerS, (n) => set('turnDegPerS', n))}
        {num('Max HP', 'maxHealth', v.maxHealth, (n) => set('maxHealth', n), { integer: true })}
        {num('Radius m', 'radiusM', v.radiusM, (n) => set('radiusM', n))}
        {num('Cargo kg', 'cargoKg', v.cargoKg, (n) => set('cargoKg', n))}
      </Fieldset>
      <Fieldset legend="Seats">
        <ListEditor<VehicleSeatDef>
          label="Seats (roster order; the first drives)"
          what="seat"
          items={v.seats ?? []}
          issues={errors.seats}
          onChange={(x) => set('seats', x)}
          make={() => ({ role: 'bed', arcDeg: 360 })}
          row={(s, i, put) => (
            <div className="grid grid-cols-[minmax(0,1fr)_7rem] gap-2">
              <div>
                <SelectInput
                  label={`Seat ${i + 1} role`}
                  value={s.role}
                  options={roleOptions}
                  onChange={(r) => put({ ...s, role: r ?? '' })}
                />
                <IssueList issues={errors[`seats.${i}.role`]} />
              </div>
              <div>
                <NumberInput
                  label={`Seat ${i + 1} arc`}
                  min={0}
                  max={360}
                  value={s.arcDeg ?? 360}
                  onChange={(n) => put({ ...s, arcDeg: n ?? 0 })}
                />
                <IssueList issues={errors[`seats.${i}.arcDeg`]} />
              </div>
            </div>
          )}
        />
        <KeyValueEditor<string>
          label="Lends weapons"
          base="lendsWeapons"
          kind="string"
          keys={SEAT_ROLES.filter((r) => r !== 'driver')}
          hint={`Seat role → weapon item the lender hands over for the run (the e-bike's pillion gets the uzi). Items: ${
            weapons.map((w) => w.value).join(', ') || 'none in the draft'
          }.`}
          value={v.lendsWeapons}
          errors={errors}
          onChange={(x) => set('lendsWeapons', x)}
        />
      </Fieldset>
      <Fieldset legend="Noise radii (m)">
        {num('Idle', 'noise.idleM', noise.idleM, (n) => set('noise', { ...noise, idleM: n }))}
        {num('Full throttle', 'noise.fullM', noise.fullM, (n) =>
          set('noise', { ...noise, fullM: n }),
        )}
        {num('Horn', 'noise.hornM', noise.hornM, (n) => set('noise', { ...noise, hornM: n }))}
        {num('Horn cooldown s', 'noise.hornCooldownS', noise.hornCooldownS, (n) =>
          set('noise', { ...noise, hornCooldownS: n }),
        )}
      </Fieldset>
      <Fieldset legend="Fuel">
        {num('Tank (s of full throttle)', 'fuel.tankS', fuel.tankS, (n) =>
          set('fuel', { ...fuel, tankS: n }),
        )}
        {num('Idle burn (share)', 'fuel.idleBurn', fuel.idleBurn, (n) =>
          set('fuel', { ...fuel, idleBurn: n }),
        )}
        <CheckboxField
          label="Electric (cans don't top it up)"
          checked={!!fuel.electric}
          onChange={(b) => set('fuel', { ...fuel, electric: b })}
        />
        <IssueList issues={errors.fuel} />
      </Fieldset>
      <Fieldset legend="Ram">
        {num("Ram speed m/s (0 = can't ram)", 'ram.minSpeedMps', ram.minSpeedMps, (n) =>
          set('ram', { ...ram, minSpeedMps: n }),
        )}
        {(ram.minSpeedMps ?? 0) > 0 ? (
          <>
            {num(
              'HP per zombie rammed',
              'ram.damageTaken',
              ram.damageTaken,
              (n) => set('ram', { ...ram, damageTaken: n }),
              { integer: true },
            )}
            {num('Speed kept per ram', 'ram.speedKeep', ram.speedKeep, (n) =>
              set('ram', { ...ram, speedKeep: n }),
            )}
            {num(
              'Brute stop HP',
              'ram.bruteDamage',
              ram.bruteDamage,
              (n) => set('ram', { ...ram, bruteDamage: n }),
              { integer: true },
            )}
          </>
        ) : (
          <>
            {num(
              'HP per hit',
              'ram.hitDamage',
              ram.hitDamage,
              (n) => set('ram', { ...ram, hitDamage: n }),
              { integer: true },
            )}
            {num('Speed lost per hit', 'ram.hitSpeedLoss', ram.hitSpeedLoss, (n) =>
              set('ram', { ...ram, hitSpeedLoss: n }),
            )}
          </>
        )}
      </Fieldset>
      <Fieldset legend="Clingers">
        {num('Max at once', 'cling.max', cling.max, (n) => set('cling', { ...cling, max: n }), {
          integer: true,
        })}
        {num('Shake turn °/s', 'cling.shakeTurnDegPerS', cling.shakeTurnDegPerS, (n) =>
          set('cling', { ...cling, shakeTurnDegPerS: n }),
        )}
        {num('Shake min speed m/s', 'cling.shakeMinSpeedMps', cling.shakeMinSpeedMps, (n) =>
          set('cling', { ...cling, shakeMinSpeedMps: n }),
        )}
      </Fieldset>
      <Fieldset legend="Mount">
        {mount ? (
          <>
            <TextField
              label="Mount weapon id"
              mono
              value={mount.id}
              issues={errors['mount.id']}
              onChange={(x) => setMount({ id: x })}
            />
            <div>
              <span className="block text-xs font-semibold uppercase tracking-wide">
                Fired from seat
              </span>
              <SelectInput
                label="Mount seat"
                value={mount.seat}
                options={roleOptions}
                onChange={(r) => setMount({ seat: r ?? '' })}
              />
              <IssueList issues={errors['mount.seat']} />
            </div>
            <TextField
              label="Base item"
              mono
              hint="The item weapon it starts from (lmg); every weapon field set below overrides it. Empty = these fields alone."
              value={mount.baseItemId ?? 'lmg'}
              issues={errors['mount.baseItemId']}
              onChange={(x) => setMount({ baseItemId: x })}
            />
            {num('Gun noise m', 'mount.noiseM', mount.noiseM, (n) => setMount({ noiseM: n }), {
              hint: "0 = the base item's.",
            })}
            {num(
              'Rounds per run',
              'mount.roundsPerRun',
              mount.roundsPerRun,
              (n) => setMount({ roundsPerRun: n }),
              { integer: true },
            )}
            {num('Damage', 'mount.weapon.damage', weapon.damage, (n) => setWeapon('damage', n))}
            {num('Range m', 'mount.weapon.rangeM', weapon.rangeM, (n) => setWeapon('rangeM', n))}
            {num('Fire interval s', 'mount.weapon.fireIntervalS', weapon.fireIntervalS, (n) =>
              setWeapon('fireIntervalS', n),
            )}
            {num(
              'Belt (magazine)',
              'mount.weapon.magazineSize',
              weapon.magazineSize,
              (n) => setWeapon('magazineSize', n),
              { integer: true },
            )}
            {num('Belt swap s', 'mount.weapon.reloadS', weapon.reloadS, (n) =>
              setWeapon('reloadS', n),
            )}
            {num('Pierce', 'mount.weapon.pierce', weapon.pierce, (n) => setWeapon('pierce', n), {
              integer: true,
            })}
            {num('Bloom per shot °', 'mount.weapon.bloomPerShotDeg', weapon.bloomPerShotDeg, (n) =>
              setWeapon('bloomPerShotDeg', n),
            )}
            {num('Max bloom °', 'mount.weapon.maxBloomDeg', weapon.maxBloomDeg, (n) =>
              setWeapon('maxBloomDeg', n),
            )}
            <div className="sm:col-span-2">
              <p className="text-xs text-bruise">
                A mount-only weapon: not an item, never carried or looted. Edit its other weapon
                fields in Edit JSON.
              </p>
              <button
                type="button"
                className={`${smallBtn} mt-1 text-blam`}
                onClick={() => set('mount', null)}
              >
                Remove the mount
              </button>
            </div>
          </>
        ) : (
          <div className="sm:col-span-2">
            <p className="text-xs text-bruise">No mount.</p>
            <button
              type="button"
              className={`${smallBtn} mt-1`}
              onClick={() => set('mount', newMount())}
            >
              Add a mount (.50 cal numbers)
            </button>
          </div>
        )}
        <IssueList issues={errors.mount} />
      </Fieldset>
    </>
  );
}
